// MCPConnectorPickerOverlay — first-class MCP connector creation overlay.
//
// Mounted at canvas root (CanvasEditor) alongside IntegrationPickerOverlay.
// Root state contract:
//   root._mcpConnectorPickerOpen  boolean — toggle visibility
//   root._mcpConnectorDraft       object  — form draft state
//   root._mcpConnectorSaving      boolean — in-flight flag
//
// On submit: calls el.call('saveMcpConnector', draft) — registered by the
// host page. Server discovers capabilities (tools/list + resources/list)
// and caches in the capabilities jsonb column.
//
// Design tokens and v3.14 syntax per CLAUDE.md:
//   - No raw CSS values — all tokens.
//   - Flat props access. Event handlers: onClick, onInput, onRender, onDestroy.
//   - placeholder: directly on input elements (no attr wrapper).
//   - Reactive CSS uses prop functions: background: (el, s) => ...
//   - State flag naming mirrors _genericIntFormOpen / _genericIntDraft.

const _DRAFT_KEY = '_mcpConnectorDraft'

// Derive a default connector name from a URL (hostname without www).
const _nameFromUrl = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '')
    return host.charAt(0).toUpperCase() + host.slice(1)
  } catch {
    return ''
  }
}

// Toast helper — mirrors IntegrationConfigureForm pattern.
const _toast = (el, payload) => {
  try { el.call('openNotification', payload) }
  catch (err) { console.warn('[mcp-connector]', payload.title, payload.message || '', err) }
}

// Submit handler. Delegates to registered project function 'saveMcpConnector'.
const _submit = async (el, s) => {
  const draft = s.root?.[_DRAFT_KEY] || {}
  if (!draft.url) {
    _toast(el, { title: 'URL required', message: 'Enter the MCP server URL.', type: 'error' })
    return
  }
  s.root.update({ _mcpConnectorSaving: true })
  try {
    await el.call('saveMcpConnector', draft)
    s.root.update({
      _mcpConnectorPickerOpen: false,
      [_DRAFT_KEY]: null,
      _mcpConnectorSaving: false
    })
    _toast(el, { title: `Connected ${draft.name || _nameFromUrl(draft.url)}`, type: 'success' })
    // Reload connector list if host exposes a loader.
    try { await el.call('reloadMcpConnectors') } catch {}
  } catch (err) {
    s.root.update({ _mcpConnectorSaving: false })
    _toast(el, {
      title: 'Connect failed',
      message: err?.message || 'Unknown error',
      type: 'error'
    })
  }
}

// ── Transport mode buttons ────────────────────────────────────────────────────

const _transportModes = ['sse', 'websocket', 'http']
const _transportLabels = { sse: 'SSE', websocket: 'WebSocket', http: 'HTTP' }

const TransportModeBtn = {
  tag: 'button',
  type: 'button',
  padding: 'V Z',
  round: 'Y',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontSize: 'Y2',
  fontWeight: '700',
  cursor: 'pointer',
  'aria-pressed': (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
    return String(active)
  },
  'aria-label': (el, s) => `Transport: ${s._label || s._mode || ''}`,
  background: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
    return active ? 'blue' : 'transparent'
  },
  color: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
    return active ? 'white' : 'title'
  },
  borderColor: 'line',
  text: (el, s) => s._label || '',
  onClick: (e, el, s) => {
    s.root.update({
      [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), transport: s._mode }
    })
  }
}

// ── Auth kind mode buttons ────────────────────────────────────────────────────

const _authModes = ['none', 'bearer', 'api-key', 'oauth']
const _authLabels = { none: 'None', bearer: 'Bearer', 'api-key': 'API key', oauth: 'OAuth' }

const AuthModeBtn = {
  tag: 'button',
  type: 'button',
  padding: 'V Z',
  round: 'Y',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontSize: 'Y2',
  fontWeight: '700',
  cursor: 'pointer',
  'aria-pressed': (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
    return String(active)
  },
  'aria-label': (el, s) => `Authentication: ${s._label || s._mode || ''}`,
  background: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
    return active ? 'blue' : 'transparent'
  },
  color: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
    return active ? 'white' : 'title'
  },
  borderColor: 'line',
  text: (el, s) => s._label || '',
  onClick: (e, el, s) => {
    s.root.update({
      [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), authKind: s._mode }
    })
  }
}

// ── Main export ───────────────────────────────────────────────────────────────

export const MCPConnectorPickerOverlay = {
  // Visibility: mirrors IntegrationPickerOverlay positioning.
  display: (el, s) => s.root?._mcpConnectorPickerOpen ? 'flex' : 'none',
  position: 'fixed',
  top: '0',
  left: '56px',
  minWidth: '280px',
  maxWidth: '400px',
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'Z1',
  theme: 'canvas-card-glass',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
  zIndex: 40,

  // Trap focus: close on Esc, return focus to AddBtn.
  onRender: (el, s) => {
    if (typeof window === 'undefined') return
    el._mcpEscHandler = (e) => {
      if (e.key === 'Escape' && s.root?._mcpConnectorPickerOpen) {
        s.root.update({ _mcpConnectorPickerOpen: false, [_DRAFT_KEY]: null })
        try {
          const addBtn = document.querySelector('[aria-label="Create project or library"]')
          if (addBtn) addBtn.focus()
        } catch {}
      }
    }
    window.addEventListener('keydown', el._mcpEscHandler)
  },
  onDestroy: (el, s) => {
    if (typeof window !== 'undefined' && el._mcpEscHandler) {
      window.removeEventListener('keydown', el._mcpEscHandler)
    }
  },

  FormTitle: {
    tag: 'h3',
    fontSize: 'A',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: 'New MCP connector'
  },

  // One-click pre-fill for Symbols MCP — the primary novel design element.
  // Background cerulean.1 (cerulean accent per design brief).
  SymbolsMcpSuggest: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    gap: 'Z',
    align: 'center',
    padding: 'V Z',
    round: 'Y',
    background: 'cerulean.1',
    color: 'cerulean',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'cerulean',
    fontSize: 'Y2',
    fontWeight: '700',
    cursor: 'pointer',
    title: 'Connect Symbols MCP — pre-fills URL and name',
    Icon: { tag: 'span', text: '⚡', fontSize: 'Y2' },
    Label: { tag: 'span', text: 'Connect Symbols MCP', fontSize: 'Y2' },
    Hint: { tag: 'span', text: 'mcp.symbols.app', fontSize: 'Y1', color: 'cerulean', opacity: '0.75' },
    onClick: (e, el, s) => {
      s.root.update({
        [_DRAFT_KEY]: {
          ...(s.root?.[_DRAFT_KEY] || {}),
          url: 'https://mcp.symbols.app',
          name: 'Symbols MCP',
          transport: 'sse',
          authKind: 'oauth'
        }
      })
    }
  },

  // ── Name field ──────────────────────────────────────────────────────

  NameField: {
    flow: 'y',
    gap: 'V',
    NameLabel: {
      tag: 'label',
      for: 'mcp-connector-name',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: 'Name'
    },
    NameInput: {
      tag: 'input',
      id: 'mcp-connector-name',
      type: 'text',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      theme: 'field',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: 'e.g. Symbols MCP',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.name || '',
      // Imperatively sync DOM input.value on every render so controlled-input
      // reactivity works in v3.14 (state-driven value updates are not automatically
      // reflected by the browser's input.value property without an explicit write).
      onRender: (el, s) => {
        if (el.node && document.activeElement !== el.node) {
          el.node.value = s.root?.[_DRAFT_KEY]?.name || ''
        }
      },
      onInput: (e, el, s) => {
        s.root.update({
          [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), name: e.target.value }
        })
      }
    }
  },

  // ── URL field ───────────────────────────────────────────────────────

  UrlField: {
    flow: 'y',
    gap: 'V',
    UrlLabel: {
      tag: 'label',
      for: 'mcp-connector-url',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: 'Server URL'
    },
    UrlInput: {
      tag: 'input',
      id: 'mcp-connector-url',
      type: 'url',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      theme: 'field',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: 'https://mcp.symbols.app',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.url || '',
      // Imperatively sync DOM input.value on every render so controlled-input
      // reactivity works in v3.14 (state-driven value updates are not automatically
      // reflected by the browser's input.value property without an explicit write).
      onRender: (el, s) => {
        if (el.node && document.activeElement !== el.node) {
          el.node.value = s.root?.[_DRAFT_KEY]?.url || ''
        }
      },
      onInput: (e, el, s) => {
        const url = e.target.value
        const draft = s.root?.[_DRAFT_KEY] || {}
        // Auto-derive name from URL if the user hasn't set one yet.
        const autoName = draft.name || _nameFromUrl(url)
        s.root.update({
          [_DRAFT_KEY]: { ...draft, url, name: autoName }
        })
      }
    }
  },

  // ── Transport picker ────────────────────────────────────────────────

  TransportField: {
    flow: 'y',
    gap: 'V',
    TransportLabel: {
      tag: 'label',
      id: 'mcp-transport-label',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: 'Transport'
    },
    TransportModes: {
      flow: 'x',
      gap: 'Z',
      role: 'group',
      'aria-labelledby': 'mcp-transport-label',
      childExtends: 'TransportModeBtn',
      childrenAs: 'state',
      children: _transportModes.map((m) => ({ _mode: m, _label: _transportLabels[m] }))
    }
  },

  // ── Auth kind picker ────────────────────────────────────────────────

  AuthField: {
    flow: 'y',
    gap: 'V',
    AuthLabel: {
      tag: 'label',
      id: 'mcp-auth-label',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: 'Authentication'
    },
    AuthModes: {
      flow: 'x',
      gap: 'Z',
      flexWrap: 'wrap',
      role: 'group',
      'aria-labelledby': 'mcp-auth-label',
      childExtends: 'AuthModeBtn',
      childrenAs: 'state',
      children: _authModes.map((m) => ({ _mode: m, _label: _authLabels[m] }))
    }
  },

  // ── Auth token input (visible for bearer/api-key) ───────────────────

  AuthTokenField: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const kind = s.root?.[_DRAFT_KEY]?.authKind || 'none'
      return kind !== 'bearer' && kind !== 'api-key'
    },
    TokenLabel: {
      tag: 'label',
      for: 'mcp-connector-auth-token',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: (el, s) => {
        const kind = s.root?.[_DRAFT_KEY]?.authKind || 'none'
        return kind === 'bearer' ? 'Bearer token' : 'API key'
      }
    },
    TokenInput: {
      tag: 'input',
      id: 'mcp-connector-auth-token',
      type: 'password',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      theme: 'field',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: '••••••••••••••••',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.authToken || '',
      onRender: (el, s) => {
        if (el.node && document.activeElement !== el.node) {
          el.node.value = s.root?.[_DRAFT_KEY]?.authToken || ''
        }
      },
      onInput: (e, el, s) => {
        s.root.update({
          [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), authToken: e.target.value }
        })
      }
    }
  },

  // ── Scope picker — reuses IntegrationScopePicker verbatim ───────────

  IntegrationScopePicker: {
    draftKey: _DRAFT_KEY,
    defaultScopeType: 'workspace'
  },

  // ── Actions row ─────────────────────────────────────────────────────

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-end',
    marginTop: 'Z',

    CancelBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Y2',
      cursor: 'pointer',
      text: 'Cancel',
      onClick: (e, el, s) => {
        s.root.update({ _mcpConnectorPickerOpen: false, [_DRAFT_KEY]: null })
        try {
          const addBtn = document.querySelector('[aria-label="Create project or library"]')
          if (addBtn) addBtn.focus()
        } catch {}
      }
    },

    ConnectBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'blue',
      color: 'white',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      disabled: (el, s) => !!(s.root?._mcpConnectorSaving),
      text: (el, s) => s.root?._mcpConnectorSaving ? 'Connecting…' : 'Connect',
      onClick: async (e, el, s) => {
        await _submit(el, s)
      }
    }
  }
}

// Re-export child components so host index.js can register them.
export { TransportModeBtn, AuthModeBtn }
